/**
 * Kraken tuning (plan E8).
 *
 * Every threshold the phase machine reads lives here, so tuning the finale is data-only
 * work. The fight-length target is 6–9 minutes; these numbers are what that target
 * decomposes into.
 */

export const KRAKEN = {
  /**
   * P0 Dread: no shootable target, deliberately.
   *
   * The game refusing to give the player anything to do about what they can hear. It is
   * the only stretch of the campaign with no valid action, and that is the content — the
   * fight is less frightening than the wait for it.
   *
   * The dread and the flattening overlap — the creature is already under the rig while
   * the storm is dying — so the sea going quiet stops being a thing that happens before
   * the arrival and becomes the arrival's own doing. (Queued one behind the other they
   * cost 210 seconds; the whole approach was 5:50 once.)
   *
   * 128, not 98, is Adam's playtest timing (2026-07-30): the sonar report lands at 2:20,
   * a beat after the blend has actually finished, and the surfacing waits a full thirty
   * seconds beyond it — fifteen for the line to land, fifteen more "so the player has
   * the time to notice the shift" in the water before the thing that caused it appears.
   * Spawn at 0:42 + 128 puts the reveal at 2:50.
   */
  dreadSeconds: 128,

  /**
   * The final assault: tentacles rear out of the water in front of the platform and hook
   * the deck edge until severed (Adam's redesign, 2026-07-30 — the fight ends at the
   * rig, not in the middle distance).
   */
  tentacleCount: 6,
  /**
   * Where the assault ring roots, metres from the origin.
   *
   * Both edges are set by what the stance can actually see. Near: a limb's root is
   * 28 m thick, so at the first staging of 62-88 m six of them subtended twenty-plus
   * degrees each and filled the frame as featureless walls — the capture showed blobs,
   * not limbs. At ~110-150 m they hold 10-13 degrees: serpents with sky between them.
   * Far: the pitch clamp (-25°) hides anything below y = 105 - 0.466·range, so at 150 m
   * everything above 35 m of height is a legal shot — the reared upper half.
   */
  assaultRangeNear: 108,
  assaultRangeFar: 150,
  /**
   * The bearings the assault may use, degrees — an arc of open sea the player can
   * actually shoot along.
   *
   * Not a taste decision. The rig masks its own sightlines, and at the elevations a
   * reared limb occupies (`__dw.maskedBearings`, sampled at -14° to +4°) the structure
   * blocks roughly -110°..-81° behind the Barracks and +10°..+57° behind the Refinery.
   * The assault used to fan 109° around the creature's own bearing, which dropped limbs
   * squarely into both — Adam sent a screenshot of one standing behind the process deck
   * with a red circle round it, unkillable from the only place the player can stand.
   *
   * This window is the widest clear sector, held inside the measured edges (-81 on one
   * side, the -3..+6 sliver of structure dead ahead on the other — four masked sectors
   * in all, pinned in kraken.test.ts). It is a property of the platform rather than of
   * the creature, so it is authored here rather than derived from where the animal
   * happens to be.
   *
   * The limbs alone are not the whole composition: the head must rise INSIDE this fan,
   * which is the surge clamp in `sim/kraken.ts` — Adam played a build where the limbs
   * stood across the window while the submerged drift had carried the body to +5°, and
   * the eye came up with no limb anywhere near it.
   */
  assaultOpenFromDeg: -78,
  assaultOpenToDeg: -8,
  /**
   * Seconds between limbs breaking the surface.
   *
   * Spawning one per simulation tick put all six in the water within a tenth of a
   * second, so the authored "ragged wave" read as one simultaneous pop. At this cadence
   * the whole assault declares itself over three and a half seconds: quick enough not to
   * soften the attack, slow enough for each water break to register.
   */
  tentacleEmergenceIntervalSeconds: 0.7,
  /**
   * Fraction of a tentacle's anti-ship damage that a grapple on the rig legs costs.
   *
   * Small, because integrity is absolute — one point is one percent of the platform.
   * Six limbs at their full anti-ship damage flatten the rig in under a minute, which
   * makes the Maw a timer rather than a fight. At this rate six limbs give the player
   * roughly two minutes to cut them off, which is the length of the phase.
   */
  tentacleRigDamageFactor: 0.011,

  /**
   * P2 Mantle: surfaced beside the rig, armoured everywhere except the eye.
   *
   * Torpedoes do the structural work — three hits ends the phase — while the eye is the
   * autocannon's only purchase on the whole creature. Giving the gun a role here matters:
   * the finale should not turn into a weapon the player has spent nine shifts not using.
   */
  mantleTorpedoHits: 3,
  /**
   * What each weapon is worth against the surfaced mantle, in torpedo-equivalents.
   *
   * The phase used to advance on torpedo hits alone, which quietly made the finale
   * unwinnable for anyone who arrived at shift 12 with fewer than six fish — and the
   * campaign never promises you will have them. Adam: "I don't want players who reach
   * level 12 with only 2 torpedoes (or less) to not be able to kill the beast."
   *
   * The ordering is the lesson, not a gate: a torpedo is the answer, a depth charge is
   * a third of one, and the gun is a last resort that works if you are willing to spend
   * the magazine on it. Against a shut mantle a shell is worth almost nothing; through
   * an open eye it is worth six times as much, so the window the slam opens is still the
   * thing worth waiting for.
   */
  mantleProgress: {
    torpedo: 1,
    depthCharge: 0.34,
    shell: 0.0016,
    shellEyeMultiplier: 6,
  },
  /** Autocannon damage multiplier on the eye. The rest of the mantle shrugs rounds off. */
  eyeDamageMultiplier: 9,
  /** Seconds the eye stays exposed after each wave slam — the counterplay window. */
  eyeWindowSeconds: 6,
  /** Seconds between wave slams while surfaced. */
  slamIntervalSeconds: 14,
  /** Integrity cost of a slam that is not answered. */
  slamIntegrity: 6,

  /**
   * P3 Submerged: it circles deep and only the hydrophone and the bulge wake tell you
   * where. Three depth-charge detonations inside the lethal radius force it back up.
   */
  submergedSeconds: 55,
  chargesToForceSurface: 3,
  /** Radius within which a depth charge counts toward forcing a resurface, metres. */
  chargeProximity: 55,
  /** Radius of its submerged circling track, metres — inside the projector's envelope. */
  orbitRadius: 780,

  /**
   * Seconds for the body to go fully under, or to come fully up.
   *
   * Adam: it "should actually move down into the water or up from it — not just poof."
   * Slow, because the mass is the point: five seconds of a six-hundred-metre back going
   * down reads as weight, and anything under about three looks like a lift descending.
   */
  diveSeconds: 5.5,
  /**
   * Metres the body drops between fully surfaced and fully under.
   *
   * Enough to carry the mantle's crown (263 m above the waterline at rest) below the
   * surface, with margin for the breathing scale. Short of this the top of the animal
   * stays in the air while the game considers it hidden.
   */
  diveDepth: 300,

  /** How many P2/P3 cycles before it stops fighting at range and comes for the rig. */
  cycles: 2,

  /**
   * The rise and the stare (Adam: "its head rises just meters in front of the player's
   * platform and we look straight into its huge eye").
   *
   * The body holds at `stareBodyRange` — with the eye 336 m forward of the body centre
   * at full head-back, that puts the eye itself about 85 m off the platform, filling the
   * scope. The stare is not a cutscene: while the head is up the creature is leaning on
   * the structure and integrity bleeds, so the shot is a decision with a cost, and a
   * player who never takes it loses the rig.
   */
  riseSeconds: 4.5,
  stareBodyRange: 470,
  /**
   * The rise waits for the body to be in this range band after the limbs die.
   *
   * Both edges are load-bearing geometry, not taste. The eye sits 336 m ahead of the
   * body centre at full head-back, so the eye stares from `range - 336`: a rise from
   * 370 m put it 34 m out — inside the rig structure — and a rise from 392 m put the
   * camera inside the cornea, because the eye itself is 74 m tall. From ~470 m it holds
   * about 130 m off the platform, where it fills a third of the sky and the head around
   * it is still in frame. Measured twice in captures, moved twice.
   */
  riseGateNear: 445,
  riseGateFar: 540,
  /** Rounds into the eye to end it — a burst, not a single lucky round. */
  eyeShotsToKill: 8,
  /** Radius of the eye as a target, metres. It is a huge eye. */
  eyeTargetRadius: 24,
  /** Integrity per second while the stare goes unanswered. */
  stareIntegrityPerSecond: 1.1,

  /**
   * Kill: the eye rolls back, the head falls away from the platform and the mass goes
   * under, then the silence beat before the spotter speaks.
   */
  eyeRollSeconds: 2.5,
  fallSeconds: 6.5,
  silenceSeconds: 6,
} as const;
